import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import type { GoogleReviewAdminItem } from "@ckgo2/shared";
import ErrorBanner from "@/components/common/ErrorBanner";
import { GOOGLE_REVIEWS_QUERY_KEY, setGoogleReviewDestination } from "./googleReviewsApi";
import { useDestinationOptions, withCurrent } from "./useDestinationOptions";

interface DestinationCorrectionDialogProps {
  item: GoogleReviewAdminItem;
  onClose: () => void;
}

/**
 * Oprava destinace Google recenze (F2.7) u exportované i ruční. Uložení destinaci zamkne
 * (i „Bez destinace" = `null`), takže ji další nahrání exportu nepřepíše. Vzhled
 * `ConfirmModal`; požadavek odchází jen z „Uložit".
 */
export default function DestinationCorrectionDialog({
  item,
  onClose,
}: DestinationCorrectionDialogProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const selectId = useId();
  const options = withCurrent(useDestinationOptions(), item);
  const [value, setValue] = useState<string>(item.destinationId ?? "");

  const saveMutation = useMutation({
    mutationFn: () => setGoogleReviewDestination(item.id, value === "" ? null : value),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [GOOGLE_REVIEWS_QUERY_KEY] });
      onClose();
    },
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${selectId}-title`}
        className="w-full max-w-md space-y-4 rounded-xl bg-white p-6 shadow-xl dark:bg-gray-800"
      >
        <h3
          id={`${selectId}-title`}
          className="text-lg font-semibold text-gray-900 dark:text-white"
        >
          {t("content.googleReviews.destinationDialog.title", { author: item.authorName })}
        </h3>
        <div>
          <label htmlFor={selectId} className="label">
            {t("content.googleReviews.destinationDialog.label")}
          </label>
          <select
            id={selectId}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            className="input"
          >
            <option value="">{t("content.googleReviews.destinationDialog.none")}</option>
            {options.map((o) => (
              <option key={o.id} value={o.id}>
                {o.label}
              </option>
            ))}
          </select>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {t("content.googleReviews.destinationDialog.hint")}
          </p>
        </div>
        <ErrorBanner error={saveMutation.error} />
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="btn-ghost"
            disabled={saveMutation.isPending}
          >
            {t("content.googleReviews.destinationDialog.cancel")}
          </button>
          <button
            type="button"
            onClick={() => saveMutation.mutate()}
            className="btn-primary"
            disabled={saveMutation.isPending}
          >
            {saveMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            {t("content.googleReviews.destinationDialog.save")}
          </button>
        </div>
      </div>
    </div>
  );
}
